/**
 * Hook för CustomerWizard
 * Hanterar state och logik för CRM-kundwizard
 */

import { useState, useCallback } from 'react';
import i18n from '@/i18n/config';
import { useNavigate } from 'react-router-dom';
import {
  WizardData,
  ContactInfo,
  INITIAL_WIZARD_DATA,
} from '@/types/customerWizard';

export function useCustomerWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<WizardData>(INITIAL_WIZARD_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Update main data fields
  const updateData = useCallback((updates: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Update contact fields
  const updateContact = useCallback((updates: Partial<ContactInfo>) => {
    setData((prev) => ({
      ...prev,
      contact: { ...prev.contact, ...updates },
    }));
  }, []);

  // Navigate to next step
  const nextStep = useCallback(() => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  }, [currentStep]);

  // Navigate to previous step
  const prevStep = useCallback(() => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  // Close wizard and navigate back
  const closeWizard = useCallback(() => {
    navigate('/crm/customers');
  }, [navigate]);

  // Submit wizard data
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/crm/customers/wizard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success) {
        navigate('/crm/customers');
      } else {
        setError(result.error || i18n.t('copy.hooks.customerCreateFailed'));
      }
    } catch {
      setError(i18n.t('copy.hooks.networkError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [data, navigate]);

  // Computed values – steg 3 kräver e-post på kontaktpersonen
  const canProceed =
    currentStep === 1 ? !!data.name :
    currentStep === 3 ? !!data.contact.email :
    true;
  const isLastStep = currentStep === 5;

  return {
    // State
    currentStep,
    data,
    isSubmitting,
    error,

    // Computed
    canProceed,
    isLastStep,

    // Actions
    updateData,
    updateContact,
    nextStep,
    prevStep,
    closeWizard,
    handleSubmit,
  };
}
